// G-03 admin MFA, the global half: every API call that gets 403 mfa_required or mfa_setup_required (lib/api.ts)
// waits here while the person verifies (or sets up two factor authentication), then the call runs again.
import * as RDialog from '@radix-ui/react-dialog'
import { ShieldCheck } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { PrivateScope } from '@/components/ui'
import { PRIVATE_AREA } from '@/lib/analytics'
import { setMfaHandler, type MfaChallenge } from '@/lib/api'
import { t } from '@/lib/i18n'
import { mfaKeys } from './api'
import { MfaSetup } from './MfaSetup'
import { MfaVerifyDialog } from './MfaVerifyDialog'

/** Full screen "Set up two factor authentication to continue" (an admin without a factor yet). */
export function MfaSetupScreen({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  return (
    <RDialog.Root open onOpenChange={(open) => !open && onCancel()}>
      <RDialog.Portal>
        <RDialog.Overlay className="fixed inset-0 z-[60] bg-page" />
        <RDialog.Content
          className="fixed inset-0 z-[61] overflow-y-auto bg-page px-4 py-10 text-text outline-none sm:py-16"
          data-testid="mfa-setup-screen"
          {...PRIVATE_AREA}
        >
          <PrivateScope>
            <div className="mx-auto w-full max-w-[560px] space-y-6">
              <div className="space-y-3 text-center">
                <span className="mx-auto flex size-12 items-center justify-center rounded-control bg-navy text-white">
                  <ShieldCheck className="size-6" aria-hidden="true" />
                </span>
                <RDialog.Title className="text-page-title text-text">{t('Set up two factor authentication to continue')}</RDialog.Title>
                <RDialog.Description className="text-body text-muted">
                  {t('Admin pages ask for a code from your phone after you log in, so your school stays safe even if someone gets your password or your email.')}
                </RDialog.Description>
              </div>
              <div className="rounded-card border border-line bg-surface p-5 shadow-card sm:p-6">
                <MfaSetup onDone={onDone} onCancel={onCancel} cancelLabel={t('Not now')} />
              </div>
            </div>
          </PrivateScope>
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  )
}

type Pending = { promise: Promise<boolean>; resolve: (passed: boolean) => void }

/** Mounted once at the top of the route tree (RootLayout). Parallel challenges share one dialog. */
export function MfaProvider() {
  const qc = useQueryClient()
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null)
  const pending = useRef<Pending | null>(null)

  useEffect(
    () =>
      setMfaHandler((next) => {
        if (pending.current) return pending.current.promise
        let resolve: (passed: boolean) => void = () => undefined
        const promise = new Promise<boolean>((r) => {
          resolve = r
        })
        pending.current = { promise, resolve }
        setChallenge(next)
        return promise
      }),
    [],
  )

  const finish = (passed: boolean) => {
    const current = pending.current
    pending.current = null
    setChallenge(null)
    if (passed) void qc.invalidateQueries({ queryKey: mfaKeys.status })
    current?.resolve(passed)
  }

  if (!challenge) return null
  if (challenge.code === 'mfa_setup_required') {
    return <MfaSetupScreen onDone={() => finish(true)} onCancel={() => finish(false)} />
  }
  return (
    <PrivateScope>
      <MfaVerifyDialog open stepUp={challenge.stepUp} onVerified={() => finish(true)} onCancel={() => finish(false)} />
    </PrivateScope>
  )
}
